import React, { useState, useEffect } from "react";
import {
  makeStyles,
  TextField,
  Grid,
  Container,
  MenuItem,
  Typography,
  Button,
} from "@material-ui/core";
import { Formik, Form, Field, FieldArray } from "formik";
import { isArray } from "lodash";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  hint: {
    marginBottom: theme.spacing(2),
  },
  // No celular cada dia quebra em duas linhas; sem separação não dá para saber
  // de qual dia são os horários.
  dayRow: {
    [theme.breakpoints.down("xs")]: {
      paddingBottom: theme.spacing(1),
      marginBottom: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    // O Grid com spacing={4} abaixo tem margem negativa e puxaria a primeira
    // linha para cima dos botões.
    marginBottom: theme.spacing(3),
  },
  dayName: {
    fontWeight: 500,
    paddingTop: theme.spacing(1.5),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
}));

const DEFAULT_SCHEDULES = [
  { weekday: "Segunda-feira", weekdayEn: "monday", startTime: "", endTime: "" },
  { weekday: "Terça-feira", weekdayEn: "tuesday", startTime: "", endTime: "" },
  { weekday: "Quarta-feira", weekdayEn: "wednesday", startTime: "", endTime: "" },
  { weekday: "Quinta-feira", weekdayEn: "thursday", startTime: "", endTime: "" },
  { weekday: "Sexta-feira", weekdayEn: "friday", startTime: "", endTime: "" },
  { weekday: "Sábado", weekdayEn: "saturday", startTime: "", endTime: "" },
  { weekday: "Domingo", weekdayEn: "sunday", startTime: "", endTime: "" },
];

/**
 * Cada dia está em um de três estados. Antes o estado era deduzido dos campos
 * de horário, e "dia em branco" significava "sem restrição" — o oposto do que
 * quem configura espera ao deixar o fim de semana vazio. Agora ele é escolhido.
 *
 * `mode` existe só no formulário. O que vai para o backend continua sendo
 * startTime/endTime, mais `closed` para o dia fechado.
 */
const MODES = [
  { value: "hours", label: "Com horário" },
  { value: "closed", label: "Fechado" },
  { value: "open", label: "Aberto 24h" },
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Sem `closed`, um dia em branco continua "sem restrição": é assim que os
// horários já salvos funcionam hoje, e abrir a tela não pode mudar isso.
//
// Janela vazia ou invertida (`00:00`–`00:00`, que é o domingo padrão das filas,
// ou `18:00`–`08:00`) nunca abre: o backend só considera dentro do expediente
// entre início e fim, então esse dia já funciona como fechado. Mostrar como
// "Com horário" travaria o salvar na validação sem o usuário ter mexido.
const modeOf = (schedule) => {
  if (schedule.closed === true) return "closed";
  if (schedule.startTime && schedule.endTime) {
    const valid =
      TIME_RE.test(schedule.startTime) && TIME_RE.test(schedule.endTime);
    if (valid && schedule.startTime >= schedule.endTime) return "closed";
    return "hours";
  }
  return "open";
};

const withMode = (list) => list.map((s) => ({ ...s, mode: modeOf(s) }));

const toPayload = (list) =>
  list.map(({ mode, ...s }) => {
    if (mode === "hours") return { ...s, closed: false };
    return { ...s, startTime: "", endTime: "", closed: mode === "closed" };
  });

// A janela precisa estar completa e na ordem certa: um horário pela metade
// chegava ao banco e quebrava a verificação de expediente de toda mensagem.
const validate = ({ schedules }) => {
  const errors = [];
  let hasErrors = false;

  schedules.forEach((s, index) => {
    if (s.mode !== "hours") return;

    const error = {};
    if (!TIME_RE.test(s.startTime || "")) error.startTime = "Informe HH:MM";
    if (!TIME_RE.test(s.endTime || "")) error.endTime = "Informe HH:MM";
    if (!error.startTime && !error.endTime && s.startTime >= s.endTime) {
      error.endTime = "Deve ser depois do início";
    }

    if (Object.keys(error).length > 0) {
      errors[index] = error;
      hasErrors = true;
    }
  });

  return hasErrors ? { schedules: errors } : {};
};

// type="time" abre o seletor do navegador (e o teclado de horário no celular);
// o valor continua no formato HH:MM que o backend já espera.
const TimeField = ({ name, label, disabled }) => (
  <Field name={name}>
    {({ field, meta }) => (
      <TextField
        label={label}
        {...field}
        type="time"
        variant="outlined"
        margin="dense"
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        error={!disabled && meta.touched && Boolean(meta.error)}
        helperText={!disabled && meta.touched ? meta.error : undefined}
        fullWidth
      />
    )}
  </Field>
);

// Replica a configuração do primeiro dia (segunda) nos seguintes, até `upTo`
// dias contando com ele, para não preencher sete linhas iguais na mão.
const copyFirstDay = (schedules, setFieldValue, upTo) => {
  const [first, ...rest] = schedules;
  const next = rest.map((s, i) =>
    i + 1 < upTo
      ? {
          ...s,
          mode: first.mode,
          startTime: first.startTime,
          endTime: first.endTime,
        }
      : s
  );
  setFieldValue("schedules", [first, ...next]);
};

function SchedulesForm(props) {
  const { initialValues, onSubmit, loading, labelSaveButton } = props;
  const classes = useStyles();

  const [schedules, setSchedules] = useState(withMode(DEFAULT_SCHEDULES));

  useEffect(() => {
    if (isArray(initialValues) && initialValues.length > 0) {
      setSchedules(withMode(initialValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleSubmit = (data) => {
    onSubmit(toPayload(data));
  };

  return (
    <Formik
      enableReinitialize
      className={classes.fullWidth}
      initialValues={{ schedules }}
      validate={validate}
      onSubmit={({ schedules }) =>
        setTimeout(() => {
          handleSubmit(schedules);
        }, 500)
      }
    >
      {({ values, setFieldValue }) => (
        <Form className={classes.fullWidth}>
          <Container>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.hint}
            >
              <strong>Com horário:</strong> atende dentro da janela informada;
              fora dela, envia a mensagem de fora do expediente.{" "}
              <strong>Fechado:</strong> envia a mensagem de fora do expediente o
              dia inteiro. <strong>Aberto 24h:</strong> atendimento normal o dia
              todo, sem mensagem de fora do expediente.
            </Typography>
          </Container>
          <Container>
            <div className={classes.toolbar}>
              <Typography variant="body2" color="textSecondary">
                Copiar {values.schedules[0]?.weekday} para:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => copyFirstDay(values.schedules, setFieldValue, 5)}
              >
                Dias úteis
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => copyFirstDay(values.schedules, setFieldValue, 7)}
              >
                Todos os dias
              </Button>
            </div>
          </Container>
          <FieldArray
            name="schedules"
            render={() => (
              <Grid spacing={4} container>
                {values.schedules.map((item, index) => {
                  const hasHours = item.mode === "hours";
                  return (
                    <Container key={item.weekdayEn || index} className={classes.dayRow}>
                      <Grid container spacing={1} alignItems="flex-start">
                        <Grid item xs={12} sm={3}>
                          <Typography className={classes.dayName}>
                            {item.weekday}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            select
                            label="Funcionamento"
                            value={item.mode}
                            onChange={(e) => {
                              const mode = e.target.value;
                              setFieldValue(`schedules[${index}].mode`, mode);
                              // O que a tela mostra é o que será salvo: fora do
                              // modo com horário, os campos ficam vazios.
                              if (mode !== "hours") {
                                setFieldValue(`schedules[${index}].startTime`, "");
                                setFieldValue(`schedules[${index}].endTime`, "");
                              } else if (!item.startTime && !item.endTime) {
                                // Já entra com um horário comercial para ajustar,
                                // em vez de dois campos vazios para digitar.
                                setFieldValue(`schedules[${index}].startTime`, "08:00");
                                setFieldValue(`schedules[${index}].endTime`, "18:00");
                              }
                            }}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          >
                            {MODES.map((m) => (
                              <MenuItem key={m.value} value={m.value}>
                                {m.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TimeField
                            name={`schedules[${index}].startTime`}
                            label="Hora Inicial"
                            disabled={!hasHours}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TimeField
                            name={`schedules[${index}].endTime`}
                            label="Hora Final"
                            disabled={!hasHours}
                          />
                        </Grid>
                      </Grid>
                    </Container>
                  );
                })}
              </Grid>
            )}
          ></FieldArray>
          <div style={{ textAlign: "center", marginTop: "2%" }} className={classes.buttonContainer}>
            <ButtonWithSpinner
              loading={loading}
              type="submit"
              color="primary"
              variant="contained"
            >
              {labelSaveButton ?? "Salvar"}
            </ButtonWithSpinner>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default SchedulesForm;
